/**
 * src/platform/imagePicker.web.ts — WEB logo image picker (WO-INV1).
 *
 * Web counterpart to imagePicker.ts. Uses a hidden <input type=file accept="image/*"> + the File
 * API to read bytes as base64. Imports NO expo-* native modules (keeps them out of the web bundle).
 *
 * NEVER imports @smartbox/* (mobile invariant 1). No PII.
 */
export type PickedImage = { contentBase64: string; mimeType: string; fileName: string };

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}

export async function pickLogoImage(): Promise<PickedImage | null> {
  const file = await new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/svg+xml';
    input.style.position = 'fixed';
    input.style.left = '-10000px';

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('focus', onFocus);
      input.remove();
    };
    const finish = (f: File | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(f);
    };
    // If the dialog is dismissed without a selection, the window regains focus with no change event.
    const onFocus = () => setTimeout(() => finish(input.files?.[0] ?? null), 350);

    input.addEventListener('change', () => finish(input.files?.[0] ?? null), { once: true });
    document.body.appendChild(input);
    window.addEventListener('focus', onFocus);
    input.click();
  });

  if (!file) return null;
  return {
    contentBase64: await fileToBase64(file),
    mimeType: file.type || 'image/png',
    fileName: file.name || 'logo.png',
  };
}
